import React, { useState } from 'react';
import { ArrowRightLeft, CheckCircle2, Loader2 } from 'lucide-react';
import { useAuth } from '../../../src/contexts/AuthContext';
import { useToast } from '../../../src/contexts/ToastContext';
import { RateioAncoraTransferencia } from '../../../types';
import { retornarTransferenciaAncora } from './ancoraTransferenciaActions';

interface AncoraTransferenciasAtivasCardProps {
  transferencias: RateioAncoraTransferencia[];
  onChanged: () => void;
}

const formatKwh = (n: number) => n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const formatData = (iso: string) => new Date(`${iso}T00:00:00`).toLocaleDateString('pt-BR');

/**
 * Transferências de titularidade da Âncora ainda ativas (Seção 5/Tela 3),
 * de todas as UGs. "Marcar como Retornado" é restrito a Gestor — mesmo
 * padrão de UcsTravadasCard.tsx.
 */
const AncoraTransferenciasAtivasCard: React.FC<AncoraTransferenciasAtivasCardProps> = ({ transferencias, onChanged }) => {
  const { profile } = useAuth();
  const { addToast } = useToast();
  const isGestor = profile?.role === 'adm' || profile?.role === 'gerente';
  const [retornandoId, setRetornandoId] = useState<string | null>(null);

  if (transferencias.length === 0) return null;

  const handleRetornar = async (transferencia: RateioAncoraTransferencia) => {
    setRetornandoId(transferencia.id);
    try {
      await retornarTransferenciaAncora(transferencia.id);
      addToast('Transferência marcada como retornada.', 'success');
      onChanged();
    } catch (error: any) {
      console.error('Error returning ancora transfer', error);
      addToast(error?.message || 'Erro ao marcar a transferência como retornada.', 'error');
    } finally {
      setRetornandoId(null);
    }
  };

  return (
    <div className="mt-6 bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
      <div className="p-4 border-b border-gray-100 dark:border-gray-700">
        <h3 className="text-sm font-semibold text-gray-800 dark:text-white flex items-center gap-1.5">
          <ArrowRightLeft size={14} className="text-primary" /> Transferências de Titularidade Ativas ({transferencias.length})
        </h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
          Saldo da Âncora emprestado a UCs, aguardando retorno agendado.
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase border-b border-gray-200 dark:border-gray-700">
              <th className="py-2 px-4">UG</th>
              <th className="py-2 px-4">UC Destino</th>
              <th className="py-2 px-4">Montante</th>
              <th className="py-2 px-4">Retorno Agendado</th>
              <th className="py-2 px-4 text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {transferencias.map(t => (
              <tr key={t.id} className="border-b border-gray-100 dark:border-gray-800">
                <td className="py-3 px-4 font-medium text-gray-900 dark:text-gray-100">
                  {t.rateio_unidades_geradoras?.nome || '—'}
                </td>
                <td className="py-3 px-4 text-gray-600 dark:text-gray-300">
                  {t.unidades_consumidoras?.numero_uc || '—'}
                  {t.unidades_consumidoras?.nome_cliente ? ` — ${t.unidades_consumidoras.nome_cliente}` : ''}
                </td>
                <td className="py-3 px-4 text-gray-900 dark:text-gray-100 font-medium">{formatKwh(t.montante_kwh)} kWh</td>
                <td className="py-3 px-4 text-gray-600 dark:text-gray-300">{formatData(t.data_retorno_agendada)}</td>
                <td className="py-3 px-4 text-right">
                  {isGestor && (
                    <button
                      type="button"
                      disabled={retornandoId === t.id}
                      onClick={() => handleRetornar(t)}
                      className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline disabled:opacity-60"
                    >
                      {retornandoId === t.id ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                      Marcar como Retornado
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AncoraTransferenciasAtivasCard;
